import * as React from 'react';
import { useSelector } from 'react-redux';
import { PermissionsType } from 'src/api';
import { Switch } from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import {
  DEACTIVATED_INFO,
  EXPIRING_INFO,
  INVITED_INFO,
  INVITED_INFO_SLACK,
  NOT_INVITED_INFO,
  NOT_INVITED_INFO_SLACK,
} from 'src/copy/users';
import { State, User } from 'src/types';
import {
  StatusBoxAction,
  StatusBoxExpiring,
  StatusBoxInfo,
  StatusBoxInvited,
  StatusBoxNotInvited,
  StatusBoxRow,
} from '.';
import * as Types from '../../../types';
import {
  LabelAndToggle,
  ModalLabelDescription,
  PermissionsLabel,
} from '../../organismsLegacy/EditUserModal';
import { AppToggleContainer } from './AppStatusSection.styles';

interface Props {
  selectedUser: Partial<User> | null;
  openDeactivateDialog?: () => void;
  openDeactivateNowDialog?: () => void;
  handleClose: () => void;
  inviteSingleUser?: (id: number) => void;
  user: User;
  handleToggle: (newToggleValue: boolean) => void;
}

export const AppStatusSection = ({
  selectedUser,
  openDeactivateDialog,
  openDeactivateNowDialog,
  handleClose,
  inviteSingleUser,
  user,
  handleToggle,
}: Props) => {
  const features = useSelector((state: State) => state.features);
  const isSlackEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.SLACK_REPORTING,
  );
  const isAppEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.APP_REPORTING,
  );
  const isSCIMOrg = useSelector(
    (state: Types.State) =>
      state.organisation.organisation?.activeScimProvider === true,
  );
  const isActiveGuestUser =
    selectedUser?.source === Types.UserSource.GUEST_USER &&
    selectedUser.status === 'NOT INVITED';
  const isNewUser = !selectedUser;

  const isNSUInScimOrg = isSCIMOrg && (isActiveGuestUser || isNewUser);

  const userStatus = () => {
    if (isNSUInScimOrg) {
      return Types.UserSource.GUEST_USER;
    }
    return selectedUser && selectedUser.status;
  };

  const handleInvite = () => {
    user.id && inviteSingleUser && inviteSingleUser(user.id);
    handleClose();
  };

  const handleDeactivateNow = () => {
    openDeactivateNowDialog && openDeactivateNowDialog();
    handleClose();
  };

  const handleDeactivate = () => {
    openDeactivateDialog && openDeactivateDialog();
    handleClose();
  };

  switch (userStatus()) {
    case 'EXPIRING':
      return (
        <React.Fragment>
          <PermissionsLabel>App Status</PermissionsLabel>
          <StatusBoxRow>
            <StatusBoxExpiring data-testid="status-box">
              EXPIRING
            </StatusBoxExpiring>
            <StatusBoxAction
              onClick={handleDeactivateNow}
              data-testid="status-box-action"
            >
              Deactivate now
            </StatusBoxAction>
          </StatusBoxRow>
          <StatusBoxInfo>{EXPIRING_INFO}</StatusBoxInfo>
        </React.Fragment>
      );
    case 'INVITED':
      return (
        <React.Fragment>
          <PermissionsLabel>Reporting invite status</PermissionsLabel>
          <StatusBoxRow>
            <StatusBoxInvited data-testid="status-box">
              INVITED
            </StatusBoxInvited>
            <StatusBoxAction
              onClick={handleDeactivate}
              data-testid="status-box-action"
            >
              Deactivate
            </StatusBoxAction>
          </StatusBoxRow>
          <StatusBoxInfo>
            {isSlackEnabled && !isAppEnabled
              ? INVITED_INFO_SLACK
              : INVITED_INFO}
          </StatusBoxInfo>
        </React.Fragment>
      );
    case 'NOT INVITED':
      return (
        <React.Fragment>
          <PermissionsLabel>Reporting invite status</PermissionsLabel>
          <StatusBoxRow>
            <StatusBoxNotInvited data-testid="status-box">
              NOT INVITED
            </StatusBoxNotInvited>
            <StatusBoxAction
              onClick={handleInvite}
              data-testid="status-box-action"
            >
              Invite
            </StatusBoxAction>
          </StatusBoxRow>
          <StatusBoxInfo>
            {isSlackEnabled && !isAppEnabled
              ? NOT_INVITED_INFO_SLACK
              : NOT_INVITED_INFO}
          </StatusBoxInfo>
        </React.Fragment>
      );
    case 'DEACTIVATED':
      return (
        <AppToggleContainer data-testid="status-box">
          <LabelAndToggle>
            <PermissionsLabel>Re-activate</PermissionsLabel>
            <Switch
              data-testid="reactivate-toggle"
              onClick={handleToggle}
              checked={user.permissions.includes(PermissionsType.APP)}
            />
          </LabelAndToggle>
          <ModalLabelDescription>{DEACTIVATED_INFO}</ModalLabelDescription>
        </AppToggleContainer>
      );
    case undefined:
    case null:
      return (
        <AppToggleContainer>
          <LabelAndToggle>
            <PermissionsLabel>Reporting invite status</PermissionsLabel>
            <Switch
              data-testid="app-toggle"
              onClick={handleToggle}
              checked={user.permissions.includes(PermissionsType.APP)}
            />
          </LabelAndToggle>
          <ModalLabelDescription>
            {isSlackEnabled && !isAppEnabled
              ? 'Enabling will grant this individual access to the Vault app in Slack.'
              : 'Enabling will grant this individual access to the Vault app.'}
          </ModalLabelDescription>
        </AppToggleContainer>
      );
    default:
      return <></>;
  }
};
